import { setLastSeenIndex } from '../actions/actions';
import {DefaultSettings} from "../../../../settings/settings-util";
import {Store} from "redux";
import {AlmeState, defaultState} from "../../../../wireup/AlmeState";
import LiveChatState from "../livechat-state";
import {AlmeUIResponseArray, Message} from "../../../../types/response";
import {HistoryMessage, HistoryMessageAlme, HistoryMessageUser} from "../../../../types/history";
import {OperatorData} from "../models/OperatorData";
import {AlmeResponseV5} from "@alme/types/response/AlmeResponse";
import {
    HistoryMessageLiveChatConnectBanner, HistoryMessageLiveChatDisconnectBanner, HistoryMessageLiveChatInput,
    HistoryMessageLiveChatQueuedBanner,
    HistoryMessageLiveChatResponse,
} from "../models/HistoryMessages";
import {receivedHistoryResponse} from "../../../initialization-gethistory/src/actions/actions";


export default class GetHistoryResponseHandlerLiveChat
{

    _settings: DefaultSettings;
    _store: Store<AlmeState> | null = null;
    _stateManager: LiveChatState;

    //#region Public Interface

    constructor(settings: DefaultSettings, stateManager: LiveChatState)
    {
        this._settings = settings;
        this._stateManager = stateManager;
    }

    initialize(store: Store<AlmeState>): void
    {
        this._store = store;
    }

    getReducer()
    {
        return (state = defaultState): AlmeState => state;
    }

    process(message: Message): Message
    {
        if (message.messageType !== this._settings.messageConstants.getHistoryRequest) { return message; }

        const responses = (message.response as AlmeUIResponseArray) ?? [];

        if (responses.length) { this.saveResponses(responses);  }

        return message;
    }

    //#endregion



    //#region Process Methods

    saveResponses(responses: AlmeUIResponseArray): void
    {
        const stateLocation = this._settings.stateLocations.History.Messages;
        const historyMessages = responses.flatMap(response => this.createHistoryMessages(response));
        const filteredHistoryMessages = this.filterPreviousQueueBanners(historyMessages);

        this._store?.dispatch(receivedHistoryResponse(filteredHistoryMessages, stateLocation));
    }

    createHistoryMessages(response: AlmeResponseV5): HistoryMessage[]
    {
        const oldLiveChatState = this._stateManager.sessionStatus;

        this.updateLiveChatState(response);
        this.updateMessageIndex(response);

        return [...this.buildQueueHistoryMessage(response),
                ...this.buildConnectHistoryMessage(response, oldLiveChatState),
                ...this.processOrigin(response),
                ...this.buildAlmeHistoryMessages(response)];
    }

    updateLiveChatState(response: AlmeResponseV5): void
    {
        const startAction = this._settings.livechat.liveChatStartAction;
        const shouldStart = !!response.responseActions?.find(action => action.Name === startAction);
        if (shouldStart)
        {
            this._stateManager.updateChatSessionState(this._settings.livechat.sessionState.queued);
        }

        if (this.isLiveChatResponse(response))
        {
            this._stateManager.updateChatSessionState(response.extensionData.SessionState as string);
        }
    }

    updateMessageIndex(response: AlmeResponseV5): void
    {
        if (typeof response.extensionData.MessageIndex !== "undefined")
        {
            const { Base, LastSeenIndex } = this._settings.stateLocations.LiveChat;
            const stateLocation = `${Base}.${LastSeenIndex}`;

            this._store?.dispatch(setLastSeenIndex(stateLocation, response.extensionData.MessageIndex as number));
        }
    }

    buildQueueHistoryMessage(response: AlmeResponseV5): HistoryMessageLiveChatQueuedBanner[]
    {
        if (this.isLiveChatResponse(response) &&
            this._stateManager.isQueuedStatus(response.extensionData.SessionState as string))
        {
            return [{ type: "LiveChatQueuedBanner" }];
        }

        return [];
    }

    buildConnectHistoryMessage(response: AlmeResponseV5, oldLiveChatState: string): HistoryMessageLiveChatConnectBanner[]
    {
        if (this.isLiveChatResponse(response) &&
            this._stateManager.isJoining(response.extensionData.SessionState as string, oldLiveChatState))
        {
            return [{ type: "LiveChatConnectBanner", operatorDataIndex: this._stateManager.getOperatorDataLength() }];
        }

        return [];
    }

    processOrigin(response: AlmeResponseV5): HistoryMessage[]
    {
        if (!this.isLiveChatResponse(response)) { return []; }

        const { livechatDisconnectBanner, livechatInputType, livechatResponseType } = this._settings.messageConstants;
        const origin = this._settings.livechat.messageOrigin;
        const { responseRevision, text, displayLinkCollection, extensionData, maskedInput } = response;
        let operatorData;

        switch (response.extensionData.MessageOrigin)
        {
            case origin.operator:
                operatorData = this._stateManager.getLatestOperatorData();

                return [{ type: livechatResponseType, responseRevision, text, displayLinkCollection, extensionData, operatorData } as HistoryMessageLiveChatResponse];

            case origin.operatorJoined:
                this._stateManager.saveOperatorData(response.extensionData.OperatorData as OperatorData);

                return [{ type: livechatResponseType, responseRevision, text, displayLinkCollection, extensionData } as HistoryMessageLiveChatResponse];

            case origin.operatorLeft:
                return [{ type: livechatResponseType, responseRevision, text, displayLinkCollection, extensionData } as HistoryMessageLiveChatResponse];

            case origin.operatorTyping:
                this._stateManager.updateOperatorTyping();
                break;

            case origin.sessionEnd:
                return [{ type: livechatResponseType, responseRevision, text, displayLinkCollection, extensionData } as HistoryMessageLiveChatResponse,
                        { type: livechatDisconnectBanner, extensionData } as HistoryMessageLiveChatDisconnectBanner];

            case origin.system:
                this._stateManager.saveQueueBannerText(response.text);
                break;

            case origin.user:
                return [{ type: livechatInputType, responseRevision, maskedInput, extensionData } as HistoryMessageLiveChatInput];
        }

        return [];
    }

    buildAlmeHistoryMessages(response: AlmeResponseV5): HistoryMessage[]
    {
        if (this.isLiveChatResponse(response)) { return []; }

        //TODO: Eventually factor this out...same code snippet from initialize-gethistory/src/response-handler.ts

        const { responseRevision, maskedInput, extensionData, text, navUrl, responseActions, displayLinkCollection } = response;
        const { userInputType, almeResponseType } = this._settings.messageConstants;

        const almeResponse: HistoryMessageAlme =
        {
            type: almeResponseType,
            responseRevision,
            text,
            navUrl,
            displayLinkCollection,
            responseActions,
            extensionData,
        };

        if ((maskedInput?.trim()?.length ?? 0) > 0)
        {
            return [{ type: userInputType, responseRevision, maskedInput, extensionData } as HistoryMessageUser, almeResponse];
        }

        return [almeResponse];
    }

    //#endregion



    //#region Helper Methods

    isLiveChatResponse(response: AlmeResponseV5): boolean
    {
        return response?.extensionData?.MessageSource === this._settings.livechat.messageSource;
    }

    filterPreviousQueueBanners(historyMessages: HistoryMessage[]): HistoryMessage[]
    {
        const { livechatQueuedBanner } = this._settings.messageConstants;
        const lastElement = historyMessages[historyMessages.length - 1].type === livechatQueuedBanner ?
            [{ type: "LiveChatQueuedBanner" }] :
            [];

        return [...historyMessages.filter((historyMessage) => historyMessage.type !== livechatQueuedBanner), ...lastElement];
    }

    //#endregion
}



// WEBPACK FOOTER //
// ./node_modules/@alme/ui-web/modules/livechat-foundation/src/handlers/gethistory-response-handler.ts